import { useEffect, useState, useContext, useMemo, lazy, Suspense } from "react";
import {
  collection, query, where, getDocs, Timestamp, getCountFromServer,
} from "firebase/firestore";
import { Users, Store, FileText, Route, ArrowLeftRight, Menu, X } from "lucide-react";

import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { db } from "../../firebase";
import { AuthContext } from "../../store/AuthContext";
import { usePerfTrace } from "../../shared/hooks";
import { getUserScope, hasScopeRestriction } from "../../shared/utils/roleScope";
import { loadFilters, mergeFilters } from "../../shared/utils/filterPersistence";
import {
  loadVisibleTiendas,
  loadVisibleVigilantes,
  loadVisibleClientes,
  loadDashboardScopedDocs,
} from "../../shared/utils/scopeData";
import { useSidebarState } from "../../components/sidebar/SidebarStateContext";
import KpiCard from "../../components/dashboard/KpiCard";
import QuickActions from "../../components/dashboard/QuickActions";
import DelegacionFilter from "../../components/dashboard/DelegacionFilter";
import { DELEGACIONES } from "../../components/dashboard/delegaciones";
import UnreadNotificationsList from "../../components/dashboard/UnreadNotificationsList";
import "./home.scss";
import "../../components/dashboard/dashboard.scss";

// Widgets operativos: cargados lazy para no bloquear el camino crítico del dashboard.
const CuadrantesPendientesList = lazy(() => import("../../components/dashboard/CuadrantesPendientesList"));
const CuadrantesDifierenList = lazy(() => import("../../components/dashboard/CuadrantesDifierenList"));
const VigilantesSinTurnosList = lazy(() => import("../../components/dashboard/VigilantesSinTurnosList"));
const PersonasConIncidenciaList = lazy(() => import("../../components/dashboard/PersonasConIncidenciaList"));
const SubactividadList = lazy(() => import("../../components/dashboard/SubactividadList"));

const WidgetFallback = () => (
  <div className="info-card">
    <ul><li className="empty">Cargando…</li></ul>
  </div>
);

const KPI_CACHE_TTL_MS = 5 * 60 * 1000;
const kpiCacheKey = (serviceId, adminId) => `dashboard_kpis_${serviceId}_${adminId || "anon"}`;

const readKpiCache = (serviceId, adminId) => {
  try {
    const raw = localStorage.getItem(kpiCacheKey(serviceId, adminId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.ts > KPI_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeKpiCache = (serviceId, adminId, data) => {
  try {
    localStorage.setItem(kpiCacheKey(serviceId, adminId), JSON.stringify({ ts: Date.now(), data }));
  } catch { /* ignore */ }
};

const startOfMonth = (d = new Date()) => {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfPrevMonth = (d = new Date()) => {
  const x = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const safeCount = async (q) => {
  try {
    const snap = await getCountFromServer(q);
    return snap.data().count || 0;
  } catch (err) {
    console.error("count error:", err);
    return null;
  }
};

const Home = () => {
  usePerfTrace("dashboard");

  const { currentUser, currentService, adminData } = useContext(AuthContext);
  const servicioId = currentService?.id || null;
  const roles = adminData?.tipoUsuario || [];
  const { collapsed } = useSidebarState();

  // La ruta /cambios-turno está limitada a estos roles; el administrativo
  // conserva la tarjeta de "Rondas de hoy".
  const puedeVerCambios = roles.includes("administrador") || roles.includes("inspector");

  const [kpis, setKpis] = useState(() => (servicioId ? readKpiCache(servicioId, adminData?.id) : null) || {
    vigilantes: null, tiendas: null, docsMes: null, docsMesAnterior: null,
    rondasHoy: null, cambiosPendientes: null,
  });
  const [isMobile, setIsMobile] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // Filtro de delegación de los widgets operativos (persistido en sesión,
  // mismo patrón que los filtros de los listados).
  const [delegacionFiltro, setDelegacionFiltro] = useState(() => {
    const saved = loadFilters("dashboard_widgets");
    return saved && saved.serviceId === servicioId ? (saved.delegacion || "") : "";
  });
  const cambiarDelegacion = (v) => {
    setDelegacionFiltro(v);
    mergeFilters("dashboard_widgets", { serviceId: servicioId, delegacion: v });
  };
  // Inspector/administrativo con delegaciones asignadas: solo las suyas.
  const opcionesDelegacion = useMemo(() => {
    const todas = Object.keys(DELEGACIONES);
    const scope = getUserScope(adminData);
    if (hasScopeRestriction(scope) && scope.delegaciones.length > 0) {
      const propias = scope.delegaciones.map(String);
      return todas.filter((c) => propias.includes(c))
        .concat(propias.filter((c) => !todas.includes(c)));
    }
    return todas;
  }, [adminData]);

  // Precalienta los datos de los widgets en cuanto hay servicio: las
  // descargas corren en paralelo con la bajada de los chunks lazy y los
  // widgets las reutilizan (todos los loaders dedupan in-flight).
  useEffect(() => {
    if (!servicioId) return;
    import("../../components/dashboard/data/dashboardPrefetch")
      .then((m) => m.prefetchDashboard(adminData, currentService))
      .catch(() => {});
  }, [servicioId, adminData, currentService]);

  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setShowContent(!mobile);
    };
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    if (!servicioId) return;
    let cancelled = false;

    const vigilantesRef = collection(db, "servicios", servicioId, "vigilantes");
    const tiendasRef = collection(db, "servicios", servicioId, "tiendas");
    const documentsRef = collection(db, "servicios", servicioId, "documents");
    const rondasRef = collection(db, "servicios", servicioId, "registrosRuta");
    const cambiosRef = collection(db, "servicios", servicioId, "solicitudesCambioTurno");

    const mesInicio = startOfMonth();
    const mesAnteriorInicio = startOfPrevMonth();
    const hoyInicio = startOfDay();
    const scope = getUserScope(adminData);

    // Mismo criterio que la página /cambios-turno: estado pendiente_inspector,
    // scopeado por inspectorCods para el inspector (filtro de estado en
    // cliente para no requerir un índice compuesto con array-contains-any).
    const contarCambiosPendientes = async () => {
      if (!puedeVerCambios) return null;
      try {
        if (scope.isInspector && !scope.isAll && scope.inspectorCods.length > 0) {
          const snap = await getDocs(query(
            cambiosRef,
            where("inspectorCods", "array-contains-any", scope.inspectorCods.slice(0, 10)),
          ));
          return snap.docs.filter((d) => d.data().estado === "pendiente_inspector").length;
        }
        return await safeCount(query(cambiosRef, where("estado", "==", "pendiente_inspector")));
      } catch (err) {
        console.error("cambios pendientes error:", err);
        return null;
      }
    };

    (async () => {
      if (scope.isAll) {
        const [vigilantes, tiendas, docsMes, docsMesAnterior, rondasHoy, cambiosPendientes] = await Promise.all([
          safeCount(query(vigilantesRef, where("borrado", "!=", true))),
          safeCount(query(tiendasRef, where("activoFreematica", "!=", false))),
          safeCount(query(documentsRef, where("fecha", ">=", Timestamp.fromDate(mesInicio)))),
          safeCount(query(
            documentsRef,
            where("fecha", ">=", Timestamp.fromDate(mesAnteriorInicio)),
            where("fecha", "<", Timestamp.fromDate(mesInicio)),
          )),
          puedeVerCambios
            ? Promise.resolve(null)
            : safeCount(query(rondasRef, where("fechaProgramada", ">=", Timestamp.fromDate(hoyInicio)))),
          contarCambiosPendientes(),
        ]);

        if (cancelled) return;
        const next = { vigilantes, tiendas, docsMes, docsMesAnterior, rondasHoy, cambiosPendientes };
        setKpis(next);
        writeKpiCache(servicioId, adminData?.id, next);
        return;
      }

      // Usuarios con scope restringido (inspector/administrativo): contamos
      // solo los elementos visibles. Más caro que getCountFromServer, pero
      // no hay forma de filtrar por delegacion/tienda en el servidor para
      // colecciones grandes.
      try {
        // Una única descarga compartida de los documentos visibles (filtrados
        // server-side por tienda/cliente) sirve a KPIs, "Última actividad",
        // donut y gráfico de actividad. Antes bajábamos toda la colección del
        // mes y del mes anterior sin límite y filtrábamos en JS.
        const [vigilantesList, tiendasList, clientesList, scopedDocs, cambiosPendientes] = await Promise.all([
          loadVisibleVigilantes(adminData, currentService),
          loadVisibleTiendas(adminData, currentService),
          loadVisibleClientes(adminData, currentService),
          loadDashboardScopedDocs(adminData, currentService),
          contarCambiosPendientes(),
        ]);

        // Las rondas (registrosRuta) no se pueden filtrar server-side por scope
        // (clienteId/tiendaId/servicioId variables); se acota por día y se
        // filtra client-side el pequeño resultado. Solo para quien ve la
        // tarjeta de rondas (administrativo).
        let rondasHoy = null;
        if (!puedeVerCambios) {
          const rondasHoySnap = await getDocs(
            query(rondasRef, where("fechaProgramada", ">=", Timestamp.fromDate(hoyInicio)))
          );
          const clienteIds = new Set(clientesList.map((c) => String(c.id)));
          const tiendaIds = new Set(tiendasList.map((t) => String(t.id)));
          const rondaMatches = (r) => {
            if (r.clienteId && clienteIds.has(String(r.clienteId))) return true;
            if (r.tiendaId && tiendaIds.has(String(r.tiendaId))) return true;
            if (r.servicioId && tiendaIds.has(String(r.servicioId))) return true;
            return false;
          };
          rondasHoy = rondasHoySnap.docs.filter((d) => rondaMatches(d.data())).length;
        }

        const mesInicioMs = mesInicio.getTime();
        const mesAnteriorInicioMs = mesAnteriorInicio.getTime();
        let docsMes = 0;
        let docsMesAnterior = 0;
        for (const d of (scopedDocs || [])) {
          const ms = d.fecha?.toMillis ? d.fecha.toMillis() : 0;
          if (ms >= mesInicioMs) docsMes += 1;
          else if (ms >= mesAnteriorInicioMs) docsMesAnterior += 1;
        }

        if (cancelled) return;
        const next = {
          vigilantes: vigilantesList.length,
          tiendas: tiendasList.length,
          docsMes,
          docsMesAnterior,
          rondasHoy,
          cambiosPendientes,
        };
        setKpis(next);
        writeKpiCache(servicioId, adminData?.id, next);
      } catch (err) {
        console.error("kpis scope error:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [servicioId, adminData, currentService]);

  const containerClass = () => {
    let base = collapsed ? "homeContainer collapsed" : "homeContainer";
    if (isMobile) base += showContent ? " visible" : "";
    return base;
  };

  const docsDelta = kpis.docsMes !== null && kpis.docsMesAnterior !== null
    ? kpis.docsMes - kpis.docsMesAnterior
    : null;

  const greetingName = adminData?.nombre
    ? `${adminData.nombre}${adminData.apellido ? " " + adminData.apellido : ""}`
    : (currentUser?.displayName || currentUser?.email || "Usuario");

  return (
    <div data-testid="sidebar1" className="home">
      <Sidebar />

      {isMobile && (
        <button
          className="mobile-toggle-btn"
          onClick={() => setShowContent((v) => !v)}
          style={{
            position: "fixed", right: "20px", top: "20px", zIndex: 1000,
            background: "#1278bd", color: "white", border: "none", borderRadius: "50%",
            width: "40px", height: "40px", display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          }}
        >
          {showContent ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}

      <div data-testid="navbar1" className={containerClass()}>
        <Navbar />

        <div className="user-greeting">
          <h2>Hola {greetingName} !</h2>
        </div>

        {/* Sección 1: KPIs */}
        <div className="kpis-grid">
          <KpiCard
            title="Vigilantes activos"
            value={kpis.vigilantes}
            to="/vigilantes"
            Icon={Users}
            tone="blue"
          />
          <KpiCard
            title="Servicios activos"
            value={kpis.tiendas}
            to="/clientes"
            Icon={Store}
            tone="dark"
          />
          <KpiCard
            title="Documentos este mes"
            value={kpis.docsMes}
            delta={docsDelta}
            to="/documentos"
            Icon={FileText}
            tone="soft"
          />
          {puedeVerCambios ? (
            <KpiCard
              title="Cambios de turno pendientes"
              value={kpis.cambiosPendientes}
              deltaLabel="Por aceptar o rechazar"
              to="/cambios-turno"
              Icon={ArrowLeftRight}
              tone="warn"
            />
          ) : (
            <KpiCard
              title="Rondas de hoy"
              value={kpis.rondasHoy}
              deltaLabel="Programadas para hoy"
              to="/control-rondas"
              Icon={Route}
              tone="warn"
            />
          )}
        </div>

        {/* Sección 2: Atajos */}
        <QuickActions roles={roles} />

        {/* Sección 3: Widgets operativos */}
        <div className="lists-toolbar">
          <DelegacionFilter
            value={delegacionFiltro}
            onChange={cambiarDelegacion}
            opciones={opcionesDelegacion}
          />
        </div>
        <div className="lists-grid">
          <Suspense fallback={<WidgetFallback />}>
            <CuadrantesPendientesList delegacion={delegacionFiltro} />
          </Suspense>
          <Suspense fallback={<WidgetFallback />}>
            <CuadrantesDifierenList delegacion={delegacionFiltro} />
          </Suspense>
          <Suspense fallback={<WidgetFallback />}>
            <VigilantesSinTurnosList delegacion={delegacionFiltro} />
          </Suspense>
          <Suspense fallback={<WidgetFallback />}>
            <PersonasConIncidenciaList delegacion={delegacionFiltro} />
          </Suspense>
          <Suspense fallback={<WidgetFallback />}>
            <SubactividadList delegacion={delegacionFiltro} />
          </Suspense>
          <UnreadNotificationsList />
        </div>
      </div>
    </div>
  );
};

export default Home;
